import React from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor, Smartphone, Tablet, Check, Info } from 'lucide-react';

export interface DeviceConfig {
  enabled: boolean;
  /** Google only: ajuste de bid em %. -100 = excluir, 0 = padrão, +300 = +300%. */
  bidAdjustment?: number;
}

export interface DevicesTargeting {
  desktop: DeviceConfig;
  mobile: DeviceConfig;
  tablet: DeviceConfig;
}

export const DEFAULT_DEVICES: DevicesTargeting = {
  desktop: { enabled: true, bidAdjustment: 0 },
  mobile: { enabled: true, bidAdjustment: 0 },
  tablet: { enabled: true, bidAdjustment: 0 },
};

interface Props {
  value: DevicesTargeting;
  onChange: (v: DevicesTargeting) => void;
  /** Quais plataformas foram selecionadas na campanha — ajusta UX (bid modifier só pra Google) */
  platforms: string[];
}

const BID_PRESETS = [-50, -25, 0, 25, 50, 100];

const DeviceTargetingInput: React.FC<Props> = ({ value, onChange, platforms }) => {
  const { t } = useTranslation();
  const hasGoogle = platforms.includes('google');
  const hasMeta = platforms.includes('meta');
  const hasLinkedIn = platforms.includes('linkedin');

  const toggle = (key: keyof DevicesTargeting) => {
    // Previne desligar todos (Meta/Google exigem ao menos 1 device)
    const nextState = !value[key].enabled;
    if (!nextState) {
      const othersEnabled = Object.entries(value).filter(([k, v]) => k !== key && v.enabled).length;
      if (othersEnabled === 0) return;
    }
    onChange({ ...value, [key]: { ...value[key], enabled: nextState } });
  };

  const setBid = (key: keyof DevicesTargeting, adj: number) => {
    onChange({ ...value, [key]: { ...value[key], bidAdjustment: adj } });
  };

  const applyPreset = (preset: 'equal' | 'mobile_first' | 'desktop_first' | 'desktop_only') => {
    if (preset === 'equal') {
      onChange({
        desktop: { enabled: true, bidAdjustment: 0 },
        mobile: { enabled: true, bidAdjustment: 0 },
        tablet: { enabled: true, bidAdjustment: 0 },
      });
    } else if (preset === 'mobile_first') {
      onChange({
        desktop: { enabled: true, bidAdjustment: 0 },
        mobile: { enabled: true, bidAdjustment: 50 },
        tablet: { enabled: true, bidAdjustment: -25 },
      });
    } else if (preset === 'desktop_first') {
      onChange({
        desktop: { enabled: true, bidAdjustment: 50 },
        mobile: { enabled: true, bidAdjustment: 0 },
        tablet: { enabled: true, bidAdjustment: -25 },
      });
    } else if (preset === 'desktop_only') {
      onChange({
        desktop: { enabled: true, bidAdjustment: 0 },
        mobile: { enabled: false, bidAdjustment: 0 },
        tablet: { enabled: false, bidAdjustment: 0 },
      });
    }
  };

  const renderDevice = (
    key: keyof DevicesTargeting,
    Icon: React.ComponentType<{ className?: string }>,
    label: string,
    description: string
  ) => {
    const cfg = value[key];
    return (
      <div
        className={`relative rounded-xl border-2 p-4 transition-all ${
          cfg.enabled
            ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/10'
            : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 opacity-60'
        }`}
      >
        <button
          type="button"
          onClick={() => toggle(key)}
          className="w-full text-left"
          aria-pressed={cfg.enabled}
        >
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                cfg.enabled
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
              }`}
            >
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-semibold ${cfg.enabled ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                  {label}
                </span>
                {cfg.enabled && <Check className="w-4 h-4 text-primary-500" />}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
            </div>
          </div>
        </button>

        {/* Google bid adjustment — só mostra se Google está na campanha e device tá enabled */}
        {cfg.enabled && hasGoogle && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                {t('campaigns.devices.bidAdjustment', 'Ajuste de lance (Google)')}
              </span>
              <span
                className={`text-sm font-bold ${
                  (cfg.bidAdjustment || 0) > 0
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : (cfg.bidAdjustment || 0) < 0
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {(cfg.bidAdjustment || 0) > 0 ? '+' : ''}{cfg.bidAdjustment || 0}%
              </span>
            </div>
            <div className="flex gap-1 flex-wrap">
              {BID_PRESETS.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setBid(key, p)}
                  className={`px-2 py-1 text-[11px] font-medium rounded-md transition-colors ${
                    (cfg.bidAdjustment || 0) === p
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {p > 0 ? '+' : ''}{p}%
                </button>
              ))}
            </div>
            <input
              type="range"
              min={-100}
              max={300}
              step={5}
              value={cfg.bidAdjustment || 0}
              onChange={(e) => setBid(key, Number(e.target.value))}
              className="w-full mt-2 accent-primary-500"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Info box: explica diferença por plataforma */}
      {(hasGoogle || hasMeta || hasLinkedIn) && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-800 dark:text-blue-300 flex gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            {hasGoogle && (
              <div>
                <strong>Google Ads:</strong> {t('campaigns.devices.infoGoogle', 'Aceita ajuste de lance por dispositivo (-100% a +300%). Usa o multiplicador na cobrança CPC.')}
              </div>
            )}
            {hasMeta && (
              <div className="mt-0.5">
                <strong>Meta Ads:</strong> {t('campaigns.devices.infoMeta', 'Seleção on/off. Tablet é contabilizado junto com mobile.')}
              </div>
            )}
            {hasLinkedIn && (
              <div className="mt-0.5">
                <strong>LinkedIn Ads:</strong> {t('campaigns.devices.infoLinkedin', 'Seleção on/off (desktop vs mobile). Tablet vai em mobile.')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Presets */}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => applyPreset('equal')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
          {t('campaigns.devices.presetEqual', 'Todos iguais')}
        </button>
        {hasGoogle && (
          <>
            <button type="button" onClick={() => applyPreset('mobile_first')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
              {t('campaigns.devices.presetMobileFirst', 'Priorizar mobile')}
            </button>
            <button type="button" onClick={() => applyPreset('desktop_first')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
              {t('campaigns.devices.presetDesktopFirst', 'Priorizar desktop')}
            </button>
          </>
        )}
        <button type="button" onClick={() => applyPreset('desktop_only')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
          {t('campaigns.devices.presetDesktopOnly', 'Só desktop')}
        </button>
      </div>

      {/* Grid 3 devices */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {renderDevice('desktop', Monitor, t('campaigns.devices.desktop', 'Desktop'), t('campaigns.devices.desktopDesc', 'Computadores e notebooks'))}
        {renderDevice('mobile', Smartphone, t('campaigns.devices.mobile', 'Smartphone'), t('campaigns.devices.mobileDesc', 'Celulares iOS e Android'))}
        {renderDevice('tablet', Tablet, t('campaigns.devices.tablet', 'Tablet'), t('campaigns.devices.tabletDesc', 'iPads e tablets Android'))}
      </div>
    </div>
  );
};

export default DeviceTargetingInput;
